import type ExcelJS from "exceljs";

const defaultBgColor = { argb: "FFFFFFFF" };
const defaultFontColor = { argb: "FF515A6E" };
const headerBgColor = { argb: "FFF1F2F5" };
const infoBgColor = { argb: "FFE7E6E6" };
const logoBgColor = { argb: "FFFFFFFF" };

const defaultFont = { name: "Helvetica Neue", family: 2, color: defaultFontColor, size: 14 };

export const silkaFont = { name: "Silka", family: 2, color: defaultFontColor, size: 14, bold: false };
export const titleFont = { ...silkaFont, bold: true, size: 20 };
export const subtitleFont = { ...silkaFont };

const defaultBorderStyle = { style: "thin" as const, color: { argb: "FFDCDCDC" } };
const defaultFillPattern = {
    type: "pattern" as const,
    pattern: "solid" as const,
    bgColor: defaultBgColor,
    fgColor: defaultBgColor
};
const defaultBorder = {
    border: {
        top: defaultBorderStyle,
        left: defaultBorderStyle,
        bottom: defaultBorderStyle,
        right: defaultBorderStyle
    }
};
// "@" is Excel's text format — it stops Excel coercing values such as "300x250"
// or a leading-zero reporting label into something else on open.
const defaultFormat = {
    numFmt: "@"
};

const styleDefaults = {
    ...defaultBorder,
    ...defaultFormat
};

/** Row 2 of every sheet: the column headers. */
export const headerRowStyle = {
    ...styleDefaults,
    fill: { ...defaultFillPattern, bgColor: headerBgColor, fgColor: headerBgColor },
    alignment: { vertical: "middle" as const, horizontal: "center" as const },
    font: {
        ...silkaFont,
        size: 14,
        italic: false,
        bold: false
    }
} as const;

/** A1, which holds the logo and must stay white rather than picking up the info fill. */
export const logoCellStyle = {
    ...headerRowStyle,
    fill: { ...defaultFillPattern, bgColor: logoBgColor, fgColor: logoBgColor }
} as const;

/** Leading identifier columns — visually distinct from the editable value columns. */
export const labelStyle = {
    ...styleDefaults,
    fill: { ...defaultFillPattern, bgColor: headerBgColor, fgColor: headerBgColor },
    alignment: { vertical: "top" as const, horizontal: "left" as const },
    font: {
        ...defaultFont,
        italic: false,
        bold: false
    }
} as const;

// Retained only for the not-yet-migrated exports in ImportExport.ts. It is an
// exact duplicate of labelStyle and should be deleted with them.
export const languageGroupNameStyle = {
    ...styleDefaults,
    ...labelStyle,
    alignment: { vertical: "top" as const, horizontal: "left" as const }
} as const;

/** Row 1: the branding band carrying the logo and the title/subtitle. */
export const infoRowStyle = {
    height: 96,
    fill: { ...defaultFillPattern, bgColor: infoBgColor, fgColor: infoBgColor },
    alignment: { vertical: "middle" as const, horizontal: "left" as const, wrapText: true },
    font: {
        ...silkaFont,
        size: 20,
        bold: true
    }
};

/** Data cells. */
export const valueStyle = {
    ...styleDefaults,
    fill: { ...defaultFillPattern },
    alignment: { vertical: "top" as const, horizontal: "left" as const },
    font: {
        ...defaultFont,
        italic: false
    }
} as const;

// `height` is a Row property rather than part of ExcelJS.Style, but infoRowStyle
// sets it, so the parameter has to admit it.
export const applyStyle = (
    element: ExcelJS.Row | ExcelJS.Cell,
    style: Partial<ExcelJS.Style> & { height?: number }
) => {
    Object.assign(element, style);
};

export const setHeaderRowStyle = (sheetObj: ExcelJS.Worksheet, rowNumber: number) => {
    const headerRow = sheetObj.getRow(rowNumber);
    applyStyle(headerRow, headerRowStyle);
};

export const setInfoRowStyle = (sheetObj: ExcelJS.Worksheet, rowNumber: number) => {
    const infoRow = sheetObj.getRow(rowNumber);
    applyStyle(infoRow, infoRowStyle);
    const infoCell = infoRow.getCell(1);
    applyStyle(infoCell, logoCellStyle);
};
